import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { classMgmtService } from "services/admin/classMgmtService";
import { userService } from "services/admin/userService";
import { venueService } from "services/admin/venueService";
import { getCurrentBackendRole } from "utils/auth";
import type {
  ClassItem,
  ClassCreate,
  ClassStatus,
  ClassListItem,
  UserListItem,
  VenueItem,
  ClassSession,
  ClassEnrollment,
  ClassEnrollCreate,
  SessionParticipant,
} from "types/domain";
import {
  MdAdd,
  MdDelete,
  MdArrowBack,
  MdPerson,
  MdCalendarMonth,
  MdInfoOutline,
  MdGroups,
  MdClose,
  MdPeople,
  MdSwapHoriz,
} from "react-icons/md";
import Card from "components/card";
import Checkbox from "components/checkbox";
import ActionButton from "components/ui/ActionButton";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import ParticipantUserAvatar from "./components/ParticipantUserAvatar";

// ── Helpers ────────────────────────────────────────────────

const STATUSES: ClassStatus[] = ["OPENING", "ONGOING", "CLOSED"];
const STATUS_LABEL_KEY: Record<ClassStatus, string> = {
  OPENING: "enum.classStatus.OPENING",
  ONGOING: "enum.classStatus.ONGOING",
  CLOSED: "enum.classStatus.CLOSED",
};

// ── Student enrollment row ─────────────────────────────────

interface EnrollRowProps {
  enrollment: ClassEnrollment;
  totalSessions: number | null;
  onTransfer?: () => void;
  onRemove: () => void;
}

const EnrollRow = ({
  enrollment,
  totalSessions,
  onTransfer,
  onRemove,
}: EnrollRowProps) => {
  const { t } = useLanguage();
  return (
    <div className="rounded-xl border border-line bg-surface px-2.5 py-2">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {enrollment.student ? (
            <ParticipantUserAvatar user={enrollment.student} size="sm" />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
              <MdPerson className="h-4 w-4" />
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold text-ink">
              {enrollment.student?.full_name ??
                enrollment.student_id.slice(0, 8)}
            </p>
            <p className="truncate text-[11px] text-ink-muted">
              {enrollment.student?.email ?? t("classForm.noEmail")}
            </p>
            <p className="mt-0.5 text-[11px] text-ink-muted">
              {t("sessionEditor.remainingSessions", {
                count: enrollment.remaining_sessions ?? totalSessions ?? "?",
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {onTransfer && (
            <ActionButton
              variant="ghost"
              size="sm"
              onClick={onTransfer}
              className="!min-h-0 !rounded-md !p-1 !text-gray-400 hover:!bg-brand-50 hover:!text-brand-500 dark:hover:!bg-brand-500/10"
              title={t("classForm.transferAction")}
            >
              <MdSwapHoriz className="h-4 w-4" />
            </ActionButton>
          )}
          <ActionButton
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="!min-h-0 !rounded-md !p-1 !text-gray-400 hover:!bg-red-50 hover:!text-red-500 dark:hover:!bg-red-900/10"
            title={t("classForm.removeFromClass")}
          >
            <MdDelete className="h-4 w-4" />
          </ActionButton>
        </div>
      </div>
    </div>
  );
};

const ACTIVE_SESSION_STATUSES = new Set(["SCHEDULED", "ACTIVE"]);

const sortSessionsByTime = (items: ClassSession[]) =>
  [...items].sort((a, b) => {
    const aTime = a.start_time
      ? new Date(a.start_time).getTime()
      : Number.MAX_SAFE_INTEGER;
    const bTime = b.start_time
      ? new Date(b.start_time).getTime()
      : Number.MAX_SAFE_INTEGER;
    return aTime - bTime;
  });

const getSelectableSessions = (items: ClassSession[]) =>
  sortSessionsByTime(
    items.filter(
      (session) =>
        session.start_time && ACTIVE_SESSION_STATUSES.has(session.status)
    )
  );

const formatSessionLabel = (
  session: ClassSession,
  t: (key: string) => string,
  locale: string
) => {
  if (!session.start_time || !session.end_time) {
    return session.title || t("classForm.unscheduledSession");
  }

  const start = new Date(session.start_time);
  const end = new Date(session.end_time);

  return `${session.title} · ${start.toLocaleDateString(
    locale
  )} ${start.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  })} - ${end.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

// ── Main form ──────────────────────────────────────────────

const ClassForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { t, locale } = useLanguage();
  const backendRole = getCurrentBackendRole();
  const canManageClass = backendRole !== "TEACHER";
  const canEnrollStudents = backendRole === "TEACHER" || canManageClass;
  const isEdit = !!id;

  // Basic info
  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [instructorIds, setInstructorIds] = React.useState<string[]>([]);
  const [classStatus, setClassStatus] = React.useState<ClassStatus>("OPENING");
  const [autoCreateSessions, setAutoCreateSessions] = React.useState(false);

  // Sessions
  const [existingSessions, setExistingSessions] = React.useState<
    ClassSession[]
  >([]);
  const [sessionDateFromFilter, setSessionDateFromFilter] = React.useState("");
  const [sessionDateToFilter, setSessionDateToFilter] = React.useState("");

  // Enrollments (edit mode)
  const [enrollments, setEnrollments] = React.useState<ClassEnrollment[]>([]);
  const [students, setStudents] = React.useState<UserListItem[]>([]);
  const [teacherKeyword, setTeacherKeyword] = React.useState("");
  const [studentKeyword, setStudentKeyword] = React.useState("");
  const [enrolledStudentSearch, setEnrolledStudentSearch] = React.useState("");
  const [studentModalOpen, setStudentModalOpen] = React.useState(false);
  const [addingStudentId, setAddingStudentId] = React.useState<string | null>(
    null
  );
  const [enrollingStudentId, setEnrollingStudentId] = React.useState<
    string | null
  >(null);
  const [remainingSessionsInput, setRemainingSessionsInput] =
    React.useState("");
  const [selectedEnrollmentSessionIds, setSelectedEnrollmentSessionIds] =
    React.useState<string[]>([]);
  const [transferEnrollment, setTransferEnrollment] =
    React.useState<ClassEnrollment | null>(null);
  const [transferTargetClassId, setTransferTargetClassId] = React.useState("");
  const [transferRemainingInput, setTransferRemainingInput] =
    React.useState("");
  const [transferSourceSessions, setTransferSourceSessions] = React.useState<
    ClassSession[]
  >([]);
  const [transferSourceSessionIds, setTransferSourceSessionIds] =
    React.useState<string[]>([]);
  const [transferTargetSessions, setTransferTargetSessions] = React.useState<
    ClassSession[]
  >([]);
  const [transferTargetSessionIds, setTransferTargetSessionIds] =
    React.useState<string[]>([]);
  const [
    transferTargetExistingEnrollment,
    setTransferTargetExistingEnrollment,
  ] = React.useState<ClassEnrollment | null>(null);
  const [
    transferTargetExistingSessionIds,
    setTransferTargetExistingSessionIds,
  ] = React.useState<string[]>([]);
  const [loadingTransferSourceSessions, setLoadingTransferSourceSessions] =
    React.useState(false);
  const [loadingTransferTargetSessions, setLoadingTransferTargetSessions] =
    React.useState(false);
  const [savingTransfer, setSavingTransfer] = React.useState(false);

  // Reference data
  const [classTeachers, setClassTeachers] = React.useState<UserListItem[]>([]);
  const [venues, setVenues] = React.useState<VenueItem[]>([]);
  const [classOptions, setClassOptions] = React.useState<ClassListItem[]>([]);
  const [classCenterId, setClassCenterId] = React.useState<string | null>(null);
  const [sessionParticipantsBySessionId, setSessionParticipantsBySessionId] =
    React.useState<Record<string, SessionParticipant[]>>({});

  const [loading, setLoading] = React.useState(isEdit);
  const [submitting, setSubmitting] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = React.useState<string | null>(null);

  // Load reference data
  React.useEffect(() => {
    const loadRef = async () => {
      const [teacherOwnerRes, managerOwnerRes, venueRes] = await Promise.all([
        userService.getAll({ role: "TEACHER", limit: 200 }),
        userService.getAll({ role: "MANAGER", limit: 200 }),
        venueService.getAll({ limit: 200 }),
      ]);
      // Class managers: TEACHER + MANAGER
      const classManagerMap = new Map<string, any>();
      [...teacherOwnerRes.items, ...managerOwnerRes.items].forEach((u) =>
        classManagerMap.set(u.id, u)
      );
      setClassTeachers(Array.from(classManagerMap.values()));
      setVenues(venueRes.items);
    };
    loadRef().catch(() => {});
  }, [isEdit]);

  // Load existing class data in edit mode
  React.useEffect(() => {
    if (!isEdit) return;
    const loadClass = async () => {
      setLoading(true);
      try {
        const [classData, sessionsData, enrollmentsData, studentRes] =
          await Promise.all([
            classMgmtService.getById(id),
            classMgmtService.getSessions(id),
            classMgmtService.getEnrollments(id),
            userService.getAll({ role: "STUDENT", limit: 200 }),
          ]);
        const c = classData as ClassItem;
        setName(c.name ?? "");
        setDescription(c.description ?? "");
        setInstructorIds(c.managers?.map((manager) => manager.id) ?? []);
        setClassStatus(c.status);
        setAutoCreateSessions(c.auto_create_sessions ?? false);
        setClassCenterId(c.center_id ?? null);
        setExistingSessions(sessionsData);
        setEnrollments(enrollmentsData);
        setStudents(studentRes.items);
      } catch (e: any) {
        setError(e.message ?? t("classForm.loadFailed"));
      } finally {
        setLoading(false);
      }
    };
    loadClass();
  }, [id, isEdit]);

  React.useEffect(() => {
    if (!isEdit) return;
    const loadClasses = async () => {
      try {
        const response = await classMgmtService.getAll({
          limit: 200,
          center_id: classCenterId ?? undefined,
        });
        setClassOptions(response.items.filter((item) => item.id !== id));
      } catch {
        setClassOptions([]);
      }
    };
    loadClasses();
  }, [classCenterId, id, isEdit]);

  const toggleClassInstructor = (teacherId: string) => {
    setInstructorIds((prev) =>
      prev.includes(teacherId)
        ? prev.filter((id) => id !== teacherId)
        : [...prev, teacherId]
    );
  };

  const selectableCurrentSessions = React.useMemo(
    () => getSelectableSessions(existingSessions),
    [existingSessions]
  );

  const ensureSessionParticipantsLoaded = React.useCallback(
    async (sessionId: string) => {
      const cached = sessionParticipantsBySessionId[sessionId];
      if (cached) return cached;
      const participants = await classMgmtService.getSessionParticipants(
        sessionId
      );
      setSessionParticipantsBySessionId((prev) => ({
        ...prev,
        [sessionId]: participants,
      }));
      return participants;
    },
    [sessionParticipantsBySessionId]
  );

  const syncStudentInSession = React.useCallback(
    async (sessionId: string, studentId: string, shouldInclude: boolean) => {
      const participants = await ensureSessionParticipantsLoaded(sessionId);
      const teacherIds = Array.from(
        new Set(
          participants
            .filter((participant) => participant.role === "teacher")
            .map((participant) => participant.user_id)
        )
      );
      const trainerIds = Array.from(
        new Set(
          participants
            .filter((participant) => participant.role === "trainer")
            .map((participant) => participant.user_id)
        )
      );
      const currentStudentIds = Array.from(
        new Set(
          participants
            .filter((participant) => participant.role === "student")
            .map((participant) => participant.user_id)
        )
      );
      const nextStudentIds = shouldInclude
        ? Array.from(new Set([...currentStudentIds, studentId]))
        : currentStudentIds.filter((id) => id !== studentId);

      const isUnchanged =
        currentStudentIds.length === nextStudentIds.length &&
        currentStudentIds.every((studentIdItem) =>
          nextStudentIds.includes(studentIdItem)
        );

      if (isUnchanged) return;

      const updatedParticipants =
        await classMgmtService.syncSessionParticipants(sessionId, {
          teacher_ids: teacherIds,
          teacher_id: teacherIds[0] ?? null,
          trainer_ids: trainerIds,
          trainer_id: trainerIds[0] ?? null,
          student_ids: nextStudentIds,
        });

      setSessionParticipantsBySessionId((prev) => ({
        ...prev,
        [sessionId]: updatedParticipants,
      }));
    },
    [ensureSessionParticipantsLoaded]
  );

  const closeTransferModal = React.useCallback(() => {
    if (savingTransfer) return;
    setTransferEnrollment(null);
    setTransferTargetClassId("");
    setTransferRemainingInput("");
    setTransferSourceSessions([]);
    setTransferSourceSessionIds([]);
    setTransferTargetSessions([]);
    setTransferTargetSessionIds([]);
    setTransferTargetExistingEnrollment(null);
    setTransferTargetExistingSessionIds([]);
  }, [savingTransfer]);

  const openTransferModal = React.useCallback(
    async (enrollment: ClassEnrollment) => {
      const currentRemaining = Math.max(0, enrollment.remaining_sessions ?? 0);
      setTransferEnrollment(enrollment);
      setTransferTargetClassId("");
      setTransferRemainingInput(String(currentRemaining));
      setTransferTargetSessions([]);
      setTransferTargetSessionIds([]);
      setLoadingTransferSourceSessions(true);

      try {
        const participantCache = { ...sessionParticipantsBySessionId };
        const missingSessionIds = selectableCurrentSessions
          .map((session) => session.id)
          .filter((sessionId) => !participantCache[sessionId]);
        if (missingSessionIds.length > 0) {
          const batchParticipants =
            await classMgmtService.getSessionParticipantsBatch(
              missingSessionIds
            );
          batchParticipants.forEach((item) => {
            participantCache[item.session_id] = item.participants;
          });
          setSessionParticipantsBySessionId((prev) => ({
            ...prev,
            ...participantCache,
          }));
        }

        const participantGroups = selectableCurrentSessions.map((session) => ({
          session,
          participants: participantCache[session.id] ?? [],
        }));

        const studentSessions = participantGroups
          .filter(({ participants }) =>
            participants.some(
              (participant) =>
                participant.role === "student" &&
                participant.user_id === enrollment.student_id
            )
          )
          .map(({ session }) => session);

        setTransferSourceSessions(studentSessions);
        setTransferSourceSessionIds(
          studentSessions.map((session) => session.id)
        );
      } catch (e: any) {
        alert(e.message ?? t("classForm.loadStudentSessionsFailed"));
        setTransferEnrollment(null);
      } finally {
        setLoadingTransferSourceSessions(false);
      }
    },
    [ensureSessionParticipantsLoaded, selectableCurrentSessions]
  );

  const handleDeleteExistingSession = async (sessionId: string) => {
    if (!window.confirm(t("sessionEditor.deleteConfirm"))) return;
    try {
      await classMgmtService.deleteSession(sessionId);
      setExistingSessions((prev) => prev.filter((s) => s.id !== sessionId));
    } catch (e: any) {
      alert(e.message ?? t("sessionEditor.deleteFailed"));
    }
  };

  const handleRemoveEnrollment = async (enrollmentId: string) => {
    if (!id || !window.confirm(t("classForm.removeStudentConfirm"))) return;
    try {
      await classMgmtService.removeEnrollment(id, enrollmentId);
      setEnrollments((prev) => prev.filter((e) => e.id !== enrollmentId));
    } catch (e: any) {
      alert(e.message ?? t("classForm.removeStudentFailed"));
    }
  };

  const handleTransferEnrollment = async () => {
    if (!id || !transferEnrollment) return;

    const currentRemaining = Math.max(
      0,
      transferEnrollment.remaining_sessions ?? 0
    );
    const transferCount = Number(transferRemainingInput);
    const keepSourceCount = transferSourceSessionIds.length;
    const remainingAfterTransfer = currentRemaining - transferCount;
    const sourceSessionIdsToRemove = transferSourceSessions
      .filter((session) => !transferSourceSessionIds.includes(session.id))
      .map((session) => session.id);

    if (!transferTargetClassId) {
      alert(t("classForm.transferTargetRequired"));
      return;
    }
    if (!Number.isFinite(transferCount) || transferCount <= 0) {
      alert(t("classForm.transferCountPositive"));
      return;
    }
    if (transferCount > currentRemaining) {
      alert(t("classForm.transferCountExceedsRemaining"));
      return;
    }
    if (remainingAfterTransfer < keepSourceCount) {
      alert(t("classForm.transferKeptExceedsRemaining"));
      return;
    }
    if (transferTargetSessionIds.length > transferCount) {
      alert(t("classForm.transferTargetExceedsCount"));
      return;
    }

    setSavingTransfer(true);
    try {
      const result = await classMgmtService.transferEnrollment(
        id,
        transferEnrollment.id,
        {
          target_class_id: transferTargetClassId,
          transferred_remaining_sessions: transferCount,
          source_session_ids_to_keep: transferSourceSessionIds,
          source_session_ids_to_remove: sourceSessionIdsToRemove,
          target_session_ids_to_add: transferTargetSessionIds,
        }
      );

      setEnrollments((prev) => {
        if (!result.source_enrollment) {
          return prev.filter((item) => item.id !== transferEnrollment.id);
        }
        return prev.map((item) =>
          item.id === transferEnrollment.id
            ? { ...item, ...result.source_enrollment, student: item.student }
            : item
        );
      });

      setSessionParticipantsBySessionId((prev) => {
        const next = { ...prev };
        sourceSessionIdsToRemove.forEach((sessionId) => {
          const participants = next[sessionId];
          if (!participants) return;
          next[sessionId] = participants.filter(
            (participant) =>
              participant.user_id !== transferEnrollment.student_id
          );
        });
        transferTargetSessionIds.forEach((sessionId) => {
          const participants = next[sessionId];
          if (!participants) return;
          const alreadyExists = participants.some(
            (participant) =>
              participant.user_id === transferEnrollment.student_id
          );
          if (alreadyExists) return;
          next[sessionId] = [
            ...participants,
            {
              id: `temp-${sessionId}-${transferEnrollment.student_id}`,
              session_id: sessionId,
              user_id: transferEnrollment.student_id,
              role: "student",
              status: null,
              user: transferEnrollment.student,
            } as SessionParticipant,
          ];
        });
        return next;
      });

      const studentName =
        transferEnrollment.student?.full_name ??
        transferEnrollment.student?.email ??
        t("enum.role.STUDENT");
      const toastParts: string[] = [
        t("classForm.transferToastMoved", { count: transferCount }),
      ];
      if (sourceSessionIdsToRemove.length > 0) {
        toastParts.push(
          t("classForm.transferToastRemoved", {
            count: sourceSessionIdsToRemove.length,
          })
        );
      }
      if (transferTargetSessionIds.length > 0) {
        toastParts.push(
          t("classForm.transferToastAdded", {
            count: transferTargetSessionIds.length,
          })
        );
      }
      addNotification(
        t("classForm.transferToast", {
          name: studentName,
          details: toastParts.join(", "),
        }),
        "success"
      );

      closeTransferModal();
    } catch (e: any) {
      alert(e.message ?? t("classForm.transferFailed"));
    } finally {
      setSavingTransfer(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      let classId = id;
      if (isEdit) {
        await classMgmtService.update(id, {
          name: name.trim() || undefined,
          description: description.trim() || null,
          manager_ids: instructorIds.length > 0 ? instructorIds : undefined,
          status: classStatus,
          auto_create_sessions: autoCreateSessions,
        });
      } else {
        if (!name.trim()) {
          throw new Error(t("classForm.nameRequired"));
        }
        const payload: ClassCreate = {
          name: name.trim(),
          description: description.trim() || undefined,
          manager_ids: instructorIds.length > 0 ? instructorIds : undefined,
          status: classStatus,
          auto_create_sessions: autoCreateSessions,
        };
        const created = (await classMgmtService.create(payload)) as ClassItem;
        classId = created.id;
      }

      navigate(`/admin/classes-mgmt/${classId}`);
    } catch (e: any) {
      setError(e.message ?? t("classForm.genericError"));
    } finally {
      setSubmitting(false);
    }
  };

  const enrolledStudentIds = new Set(enrollments.map((e) => e.student_id));
  const availableStudents = students.filter(
    (s) => !enrolledStudentIds.has(s.id)
  );
  const confirmEnrollStudent = async (studentId: string) => {
    if (!id || enrolledStudentIds.has(studentId)) return;
    const remaining =
      remainingSessionsInput.trim() === ""
        ? undefined
        : Number(remainingSessionsInput);
    if (
      remainingSessionsInput.trim() !== "" &&
      (Number.isNaN(remaining) || (remaining as number) < 0)
    ) {
      alert(t("classForm.remainingNonNegative"));
      return;
    }
    if (
      remaining !== undefined &&
      selectedEnrollmentSessionIds.length > remaining
    ) {
      alert(t("classForm.selectedExceedsRemaining"));
      return;
    }

    setAddingStudentId(studentId);
    try {
      const data: ClassEnrollCreate = {
        student_id: studentId,
        remaining_sessions: remaining,
      };
      const result = await classMgmtService.enroll(id, data);
      await Promise.all(
        selectedEnrollmentSessionIds.map((sessionId) =>
          syncStudentInSession(sessionId, studentId, true)
        )
      );
      const studentInfo = students.find((s) => s.id === studentId);
      setEnrollments((prev) => [...prev, { ...result, student: studentInfo }]);
      setAddingStudentId(null);
      setEnrollingStudentId(null);
      setRemainingSessionsInput("");
      setStudentKeyword("");
      setSelectedEnrollmentSessionIds([]);
      setStudentModalOpen(false);
    } catch (e: any) {
      alert(e.message ?? t("classForm.addStudentFailed"));
      setAddingStudentId(null);
    }
  };

  const closeStudentModal = () => {
    if (!addingStudentId) {
      setStudentModalOpen(false);
    }
  };
  const selectedClassTeachers = React.useMemo(
    () =>
      classTeachers.filter((teacher) =>
        instructorIds.includes(String(teacher.id))
      ),
    [classTeachers, instructorIds]
  );
  const filteredClassTeachers = React.useMemo(() => {
    const keyword = teacherKeyword.trim().toLowerCase();
    if (!keyword) return classTeachers;
    return classTeachers.filter((teacher) => {
      const name = (teacher.full_name ?? "").toLowerCase();
      const email = (teacher.email ?? "").toLowerCase();
      return name.includes(keyword) || email.includes(keyword);
    });
  }, [classTeachers, teacherKeyword]);
  const filteredAvailableStudents = React.useMemo(() => {
    const keyword = studentKeyword.trim().toLowerCase();
    if (!keyword) return availableStudents;
    return availableStudents.filter((student) => {
      const name = (student.full_name ?? "").toLowerCase();
      return name.includes(keyword);
    });
  }, [availableStudents, studentKeyword]);
  const filteredEnrolledStudents = React.useMemo(() => {
    const keyword = enrolledStudentSearch.trim().toLowerCase();
    if (!keyword) return enrollments;
    return enrollments.filter((enrollment) => {
      const name = (enrollment.student?.full_name ?? "").toLowerCase();
      return name.includes(keyword);
    });
  }, [enrolledStudentSearch, enrollments]);
  const filteredExistingSessions = React.useMemo(() => {
    return existingSessions.filter((session) => {
      const sessionDate = session.start_time
        ? new Date(session.start_time).toISOString().slice(0, 10)
        : "";
      const matchesFrom =
        !sessionDateFromFilter ||
        (sessionDate !== "" && sessionDate >= sessionDateFromFilter);
      const matchesTo =
        !sessionDateToFilter ||
        (sessionDate !== "" && sessionDate <= sessionDateToFilter);
      return matchesFrom && matchesTo;
    });
  }, [existingSessions, sessionDateFromFilter, sessionDateToFilter]);
  const targetClassOptions = React.useMemo(
    () => classOptions.filter((classItem) => classItem.status !== "CLOSED"),
    [classOptions]
  );
  const transferCurrentRemaining = Math.max(
    0,
    transferEnrollment?.remaining_sessions ?? 0
  );
  const transferTargetCurrentRemaining = Math.max(
    0,
    transferTargetExistingEnrollment?.remaining_sessions ?? 0
  );
  React.useEffect(() => {
    if (!transferEnrollment) return;
    const defaultRemaining = String(
      Math.max(0, transferEnrollment.remaining_sessions ?? 0)
    );
    setTransferRemainingInput((prev) =>
      prev.trim() === "" ? defaultRemaining : prev
    );
  }, [transferEnrollment?.id]);
  const transferCountValue =
    transferRemainingInput.trim() === "" ? 0 : Number(transferRemainingInput);
  const remainingAfterTransfer = Number.isFinite(transferCountValue)
    ? transferCurrentRemaining - transferCountValue
    : transferCurrentRemaining;
  const targetRemainingAfterTransfer =
    Number.isFinite(transferCountValue) && transferCountValue > 0
      ? transferTargetCurrentRemaining + transferCountValue
      : transferTargetCurrentRemaining;
  const canSubmitTransfer =
    !!transferEnrollment &&
    !!transferTargetClassId &&
    Number.isFinite(transferCountValue) &&
    transferCountValue > 0 &&
    transferCountValue <= transferCurrentRemaining;
  React.useEffect(() => {
    if (!studentModalOpen) {
      setAddingStudentId(null);
      setEnrollingStudentId(null);
      setRemainingSessionsInput("");
      setStudentKeyword("");
      setSelectedEnrollmentSessionIds([]);
    }
  }, [studentModalOpen]);

  React.useEffect(() => {
    if (!transferEnrollment || !transferTargetClassId) {
      setTransferTargetSessions([]);
      setTransferTargetSessionIds([]);
      setTransferTargetExistingEnrollment(null);
      setTransferTargetExistingSessionIds([]);
      return;
    }

    const loadTargetSessions = async () => {
      setLoadingTransferTargetSessions(true);
      try {
        const [sessions, targetEnrollments] = await Promise.all([
          classMgmtService.getSessions(transferTargetClassId),
          classMgmtService.getEnrollments(transferTargetClassId),
        ]);
        const selectableSessions = getSelectableSessions(sessions);
        const targetEnrollment =
          targetEnrollments.find(
            (enrollment) =>
              enrollment.student_id === transferEnrollment.student_id
          ) ?? null;
        const targetParticipantGroups =
          await classMgmtService.getSessionParticipantsBatch(
            selectableSessions.map((session) => session.id)
          );
        const existingSessionIds = targetParticipantGroups
          .filter((group) =>
            group.participants.some(
              (participant: SessionParticipant) =>
                participant.role === "student" &&
                participant.user_id === transferEnrollment.student_id
            )
          )
          .map((group) => group.session_id);
        setTransferTargetSessions(selectableSessions);
        setTransferTargetExistingEnrollment(targetEnrollment);
        setTransferTargetExistingSessionIds(existingSessionIds);
        setSessionParticipantsBySessionId((prev) => {
          const next = { ...prev };
          targetParticipantGroups.forEach((group) => {
            next[group.session_id] = group.participants;
          });
          return next;
        });
        setTransferTargetSessionIds([]);
      } catch {
        setTransferTargetSessions([]);
        setTransferTargetSessionIds([]);
        setTransferTargetExistingEnrollment(null);
        setTransferTargetExistingSessionIds([]);
      } finally {
        setLoadingTransferTargetSessions(false);
      }
    };

    loadTargetSessions();
  }, [transferEnrollment, transferTargetClassId]);

  if (loading) {
    return (
      <div className="mt-5 flex items-center justify-center py-20">
        <p className="text-sm text-ink-subtle">{t("state.loading")}</p>
      </div>
    );
  }

  if (!canManageClass && !isEdit) {
    return (
      <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:border-amber-900/50 dark:bg-amber-900/10 dark:text-amber-400">
        {t("classForm.noCreatePermission")}
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-6">
      <div className="rounded-3xl border border-line bg-surface px-5 py-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-3">
            <ActionButton
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="!rounded-xl !border !border-gray-200 !p-2 !text-gray-500 hover:!bg-gray-50 dark:!border-line dark:!text-gray-400 dark:hover:!bg-navy-700"
            >
              <MdArrowBack className="h-5 w-5" />
            </ActionButton>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-2xl font-bold text-ink">
                  {isEdit
                    ? t("classForm.editTitle")
                    : t("classForm.createTitle")}
                </h2>
                <span className="rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                  {isEdit
                    ? t("classForm.editModeBadge")
                    : t("classForm.createModeBadge")}
                </span>
              </div>
              <p className="mt-1 text-sm text-ink-muted">
                {t("classForm.pageSubtitle")}
              </p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {canManageClass && (
          <>
            <Card extra="p-6">
              <div className="mb-5 flex items-start gap-3">
                <div className="rounded-2xl bg-brand-50 p-2.5 text-brand-500 dark:bg-brand-500/10 dark:text-brand-300">
                  <MdInfoOutline className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-ink">
                    {t("classForm.basicInfoTitle")}
                  </h3>
                  <p className="mt-1 text-sm text-ink-muted">
                    {t("classForm.basicInfoSubtitle")}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(260px,320px)]">
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div className="md:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-ink-muted">
                        {t("classForm.nameLabel")}{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        placeholder={t("classForm.namePlaceholder")}
                        className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-500"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-ink-muted">
                        {t("classForm.statusLabel")}
                      </label>
                      <select
                        value={classStatus}
                        onChange={(e) =>
                          setClassStatus(e.target.value as ClassStatus)
                        }
                        className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-500"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {t(STATUS_LABEL_KEY[s])}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="rounded-2xl border border-line bg-gray-50 px-4 py-3 dark:bg-canvas/30">
                      <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                        {t("classForm.autoCreateSessions")}
                      </p>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <span className="text-sm text-ink">
                          {t("classForm.autoCreateSessionsHint")}
                        </span>
                        <label className="relative inline-flex cursor-pointer items-center">
                          <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={autoCreateSessions}
                            onChange={(e) =>
                              setAutoCreateSessions(e.target.checked)
                            }
                          />
                          <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-brand-500 peer-checked:after:translate-x-full peer-checked:after:border-white dark:bg-surface-muted" />
                        </label>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-ink-muted">
                      {t("classForm.descriptionLabel")}
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      placeholder={t("classForm.descriptionPlaceholderLong")}
                      rows={4}
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2.5 text-sm text-ink outline-none focus:border-brand-500"
                    />
                  </div>
                </div>

                <div className="rounded-2xl border border-line bg-gray-50/70 p-4 dark:bg-canvas/30">
                  <div className="flex items-center gap-2">
                    <MdGroups className="h-5 w-5 text-brand-500" />
                    <div>
                      <h4 className="text-sm font-semibold text-ink">
                        {t("classForm.managersTitle")}
                      </h4>
                      <p className="text-xs text-ink-muted">
                        {t("classForm.managersSubtitle")}
                      </p>
                    </div>
                  </div>

                  <input
                    value={teacherKeyword}
                    onChange={(e) => setTeacherKeyword(e.target.value)}
                    placeholder={t("classForm.searchNameOrEmail")}
                    className="mt-4 w-full rounded-xl border border-line px-3 py-2 text-sm text-ink outline-none focus:border-brand-500 dark:bg-surface-muted"
                  />

                  <div className="mt-3 max-h-[260px] space-y-1 overflow-y-auto pr-1">
                    {filteredClassTeachers.length === 0 ? (
                      <p className="py-6 text-center text-xs text-ink-muted">
                        {t("classForm.noMatchingResults")}
                      </p>
                    ) : (
                      filteredClassTeachers.map((teacher) => {
                        const teacherId = String(teacher.id);
                        const checked = instructorIds.includes(teacherId);
                        return (
                          <label
                            key={teacherId}
                            className="border-transparent flex cursor-pointer items-center justify-between rounded-xl border bg-surface px-3 py-2 hover:border-brand-200 hover:bg-brand-50/40 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/10"
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <Checkbox
                                color="orange"
                                checked={checked}
                                onChange={() =>
                                  toggleClassInstructor(teacherId)
                                }
                              />
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-ink">
                                  {teacher.full_name ??
                                    teacher.email ??
                                    t("classForm.unknownUser")}
                                </p>
                                <p className="truncate text-xs text-ink-muted">
                                  {teacher.email ?? t("classForm.noEmail")}
                                </p>
                              </div>
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>

                  <div className="mt-4 border-t border-line pt-3">
                    <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                      {t("classForm.selectedCount", {
                        count: selectedClassTeachers.length,
                      })}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedClassTeachers.length === 0 ? (
                        <span className="text-xs text-ink-subtle">
                          {t("classForm.noManagerSelected")}
                        </span>
                      ) : (
                        selectedClassTeachers.map((teacher) => (
                          <ActionButton
                            key={teacher.id}
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              toggleClassInstructor(String(teacher.id))
                            }
                            className="!inline-flex !min-h-0 !gap-1 !rounded-full !bg-orange-100 !px-2.5 !py-1 !text-xs !font-medium !text-orange-700 hover:!bg-orange-200 dark:!bg-orange-900/30 dark:!text-orange-300"
                          >
                            {teacher.full_name ??
                              teacher.email ??
                              t("classForm.unknownUser")}
                            <span aria-hidden>×</span>
                          </ActionButton>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}

        {isEdit && canEnrollStudents && (
          <Card extra="p-6">
            <div className="mb-5 flex items-start gap-3">
              <div className="rounded-2xl bg-brand-50 p-2.5 text-brand-500 dark:bg-brand-500/10 dark:text-brand-300">
                <MdPeople className="h-5 w-5" />
              </div>
              <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center">
                  <h3 className="flex-shrink-0 text-base font-semibold text-ink">
                    {t("enum.role.STUDENT")}
                    <span className="ml-2 rounded-full bg-surface-muted px-2 py-0.5 text-xs font-normal text-gray-600 dark:text-gray-400">
                      {enrollments.length}
                    </span>
                  </h3>
                  <input
                    type="text"
                    value={enrolledStudentSearch}
                    onChange={(e) => setEnrolledStudentSearch(e.target.value)}
                    placeholder={t("classForm.searchStudentByName")}
                    className="w-full max-w-md rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                  />
                </div>
                <ActionButton
                  variant="primary"
                  size="sm"
                  onClick={() => setStudentModalOpen(true)}
                  startIcon={<MdAdd className="h-4 w-4" />}
                >
                  {t("classForm.addStudent")}
                </ActionButton>
              </div>
            </div>

            <div className="mt-5 border-t border-line pt-5">
              {enrollments.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-subtle">
                  {t("classForm.noStudents")}
                </p>
              ) : filteredEnrolledStudents.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-subtle">
                  {t("classForm.noMatchingStudents")}
                </p>
              ) : (
                <div className="max-h-[640px] overflow-y-auto pr-1">
                  <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                    {filteredEnrolledStudents.map((e) => (
                      <EnrollRow
                        key={e.id}
                        enrollment={e}
                        totalSessions={null}
                        onTransfer={
                          canManageClass
                            ? () => openTransferModal(e)
                            : undefined
                        }
                        onRemove={() => handleRemoveEnrollment(e.id)}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {studentModalOpen && (
          <div
            className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={closeStudentModal}
          >
            <div
              className="w-full max-w-2xl rounded-2xl bg-surface p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-bold text-ink">
                    {t("classForm.addStudentModalTitle")}
                  </h4>
                  <p className="mt-1 text-sm text-ink-muted">
                    {t("classForm.addStudentModalSubtitle")}
                  </p>
                </div>
                <ActionButton
                  variant="ghost"
                  size="sm"
                  onClick={closeStudentModal}
                  disabled={Boolean(addingStudentId)}
                  className="!min-h-0 !rounded-lg !p-1 !text-gray-400 hover:!bg-gray-100 dark:hover:!bg-white/10"
                >
                  <MdClose className="h-5 w-5" />
                </ActionButton>
              </div>

              <div className="mb-4">
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  {t("classForm.searchStudentLabel")}
                </label>
                <input
                  type="text"
                  value={studentKeyword}
                  onChange={(e) => setStudentKeyword(e.target.value)}
                  placeholder={t("sessionEditor.searchStudentsPlaceholder")}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                />
              </div>

              {filteredAvailableStudents.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-muted">
                  {t("classForm.noMatchingStudents")}
                </p>
              ) : (
                <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
                  {filteredAvailableStudents.map((student) => {
                    const alreadyEnrolled = enrolledStudentIds.has(student.id);
                    const isAdding = addingStudentId === student.id;
                    const isEnrolling = enrollingStudentId === student.id;

                    return (
                      <div
                        key={student.id}
                        className="rounded-xl border border-line bg-white dark:bg-surface-muted/60"
                      >
                        <div className="flex items-center justify-between gap-3 px-3 py-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <ParticipantUserAvatar user={student} size="md" />
                            <div className="min-w-0">
                              <p className="truncate text-sm font-medium text-ink">
                                {student.full_name ??
                                  student.email ??
                                  t("enum.role.STUDENT")}
                              </p>
                              <p className="truncate text-xs text-ink-muted">
                                {student.email ?? t("classForm.noEmail")}
                              </p>
                            </div>
                          </div>
                          {alreadyEnrolled ? (
                            <span className="ml-3 flex-shrink-0 rounded-full bg-gray-200 px-3 py-1.5 text-xs font-semibold text-ink-muted dark:bg-surface-hover">
                              {t("classForm.alreadyInClass")}
                            </span>
                          ) : isEnrolling ? null : (
                            <ActionButton
                              variant="primary"
                              size="sm"
                              onClick={() => {
                                setEnrollingStudentId(student.id);
                                setRemainingSessionsInput("");
                              }}
                              className="!ml-3 !flex-shrink-0"
                            >
                              {t("action.add")}
                            </ActionButton>
                          )}
                        </div>

                        {isEnrolling && (
                          <div className="border-t border-line px-3 pb-3 pt-2.5">
                            <div className="grid grid-cols-1 gap-3 lg:grid-cols-[220px,minmax(0,1fr)]">
                              <div>
                                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                                  {t("classForm.remainingSessionsLabel")}{" "}
                                  <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  value={remainingSessionsInput}
                                  onChange={(e) =>
                                    setRemainingSessionsInput(e.target.value)
                                  }
                                  placeholder={t(
                                    "classForm.remainingSessionsPlaceholder"
                                  )}
                                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink outline-none placeholder:text-gray-400 focus:border-brand-500"
                                />
                              </div>
                              <div>
                                <p className="mb-1 text-xs font-medium text-gray-600 dark:text-gray-400">
                                  {t("classForm.quickAddToSessions", {
                                    selected:
                                      selectedEnrollmentSessionIds.length,
                                    total: selectableCurrentSessions.length,
                                  })}
                                </p>
                                {selectableCurrentSessions.length === 0 ? (
                                  <div className="rounded-lg border border-dashed border-line px-3 py-4 text-xs text-ink-muted">
                                    {t("classForm.noScheduledSessionsToPick")}
                                  </div>
                                ) : (
                                  <div className="max-h-[220px] space-y-2 overflow-y-auto rounded-lg border border-line p-2">
                                    {selectableCurrentSessions.map(
                                      (session) => {
                                        const checked =
                                          selectedEnrollmentSessionIds.includes(
                                            session.id
                                          );
                                        return (
                                          <label
                                            key={session.id}
                                            className="flex cursor-pointer items-start gap-2 rounded-lg px-2 py-1.5 hover:bg-surface-hover"
                                          >
                                            <input
                                              type="checkbox"
                                              checked={checked}
                                              onChange={(e) => {
                                                setSelectedEnrollmentSessionIds(
                                                  (prev) =>
                                                    e.target.checked
                                                      ? [...prev, session.id]
                                                      : prev.filter(
                                                          (id) =>
                                                            id !== session.id
                                                        )
                                                );
                                              }}
                                              className="mt-0.5"
                                            />
                                            <span className="text-xs text-gray-600 dark:text-gray-300">
                                              {formatSessionLabel(
                                                session,
                                                t,
                                                locale
                                              )}
                                            </span>
                                          </label>
                                        );
                                      }
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="mt-3 flex justify-end gap-2">
                              <ActionButton
                                variant="secondary"
                                size="sm"
                                onClick={() => {
                                  setEnrollingStudentId(null);
                                  setRemainingSessionsInput("");
                                  setSelectedEnrollmentSessionIds([]);
                                }}
                                disabled={isAdding}
                              >
                                {t("action.cancel")}
                              </ActionButton>
                              <ActionButton
                                variant="primary"
                                size="sm"
                                onClick={() => confirmEnrollStudent(student.id)}
                                disabled={
                                  isAdding ||
                                  remainingSessionsInput.trim() === ""
                                }
                              >
                                {isAdding
                                  ? t("classForm.adding")
                                  : t("action.confirm")}
                              </ActionButton>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {transferEnrollment && (
          <div
            className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={closeTransferModal}
          >
            <div
              className="w-full max-w-5xl rounded-2xl bg-surface p-6 shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-lg font-bold text-ink">
                    {t("classForm.transferTitle")}
                  </h4>
                  <p className="mt-1 text-sm text-ink-muted">
                    {t("classForm.transferSubtitle")}
                  </p>
                </div>
                <ActionButton
                  variant="ghost"
                  size="sm"
                  onClick={closeTransferModal}
                  disabled={savingTransfer}
                  className="!min-h-0 !rounded-lg !p-1 !text-gray-400 hover:!bg-gray-100 dark:hover:!bg-white/10"
                >
                  <MdClose className="h-5 w-5" />
                </ActionButton>
              </div>

              <div className="mb-4 rounded-2xl border border-line bg-gray-50/70 p-4 dark:bg-canvas/20">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-3">
                    {transferEnrollment.student ? (
                      <ParticipantUserAvatar
                        user={transferEnrollment.student}
                        size="md"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300">
                        <MdPerson className="h-4 w-4" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold text-ink">
                        {transferEnrollment.student?.full_name ??
                          transferEnrollment.student_id}
                      </p>
                      <p className="text-xs text-ink-muted">
                        {transferEnrollment.student?.email ??
                          t("classForm.noEmail")}
                      </p>
                    </div>
                  </div>
                  <span className="rounded-full bg-surface px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300">
                    {t("classForm.transferCurrentRemaining", {
                      count: transferCurrentRemaining,
                    })}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-[280px,minmax(0,1fr),minmax(0,1fr)]">
                <div className="space-y-4 rounded-2xl border border-line p-4">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      {t("classForm.transferTargetLabel")}
                    </label>
                    <select
                      value={transferTargetClassId}
                      onChange={(e) => setTransferTargetClassId(e.target.value)}
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                    >
                      <option value="">
                        {t("classForm.transferTargetPlaceholder")}
                      </option>
                      {targetClassOptions.map((classItem) => (
                        <option key={classItem.id} value={classItem.id}>
                          {classItem.name}
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-ink-muted">
                      {t("classForm.transferTargetRemaining", {
                        count: transferTargetCurrentRemaining,
                      })}
                    </p>
                    {transferTargetExistingEnrollment && (
                      <div className="mt-2 rounded-lg bg-brand-50 px-3 py-2 text-xs font-medium text-brand-700 dark:bg-brand-500/10 dark:text-brand-300">
                        {t("classForm.transferTargetExisting", {
                          count: transferTargetCurrentRemaining,
                        })}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                      {t("classForm.transferCountLabel")}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max={transferCurrentRemaining}
                      value={transferRemainingInput}
                      onChange={(e) =>
                        setTransferRemainingInput(e.target.value)
                      }
                      onBlur={() => {
                        if (!transferEnrollment) return;
                        if (transferRemainingInput.trim() !== "") return;
                        setTransferRemainingInput(
                          String(
                            Math.max(
                              0,
                              transferEnrollment.remaining_sessions ?? 0
                            )
                          )
                        );
                      }}
                      placeholder={String(transferCurrentRemaining)}
                      className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                    />
                  </div>

                  <div className="rounded-xl bg-gray-50 px-3 py-3 text-xs text-gray-600 dark:bg-surface-muted/60 dark:text-gray-300">
                    <p>
                      {t("classForm.transferSummaryRemaining", {
                        count: Number.isFinite(remainingAfterTransfer)
                          ? remainingAfterTransfer
                          : "-",
                      })}
                    </p>
                    <p className="mt-1">
                      {t("classForm.transferSummaryKept", {
                        count: transferSourceSessionIds.length,
                      })}
                    </p>
                    <p className="mt-1">
                      {t("classForm.transferSummaryTarget", {
                        count: targetRemainingAfterTransfer,
                      })}
                    </p>
                  </div>
                </div>

                <div className="rounded-2xl border border-line p-4">
                  <div className="mb-3">
                    <h5 className="text-sm font-semibold text-ink">
                      {t("classForm.transferSourceSessionsTitle")}
                    </h5>
                    <p className="mt-1 text-xs text-ink-muted">
                      {t("classForm.transferSourceSessionsHint")}
                    </p>
                  </div>
                  {loadingTransferSourceSessions ? (
                    <p className="py-6 text-center text-sm text-ink-subtle">
                      {t("classForm.loadingSessions")}
                    </p>
                  ) : transferSourceSessions.length === 0 ? (
                    <p className="py-6 text-center text-sm text-ink-subtle">
                      {t("classForm.studentHasNoScheduledSessions")}
                    </p>
                  ) : (
                    <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                      {transferSourceSessions.map((session) => {
                        const checked = transferSourceSessionIds.includes(
                          session.id
                        );
                        return (
                          <label
                            key={session.id}
                            className="flex cursor-pointer items-start gap-2 rounded-xl border border-line px-3 py-2 hover:bg-surface-hover"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setTransferSourceSessionIds((prev) =>
                                  e.target.checked
                                    ? [...prev, session.id]
                                    : prev.filter(
                                        (idItem) => idItem !== session.id
                                      )
                                );
                              }}
                              className="mt-1"
                            />
                            <span className="text-xs text-gray-600 dark:text-gray-300">
                              {formatSessionLabel(session, t, locale)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="rounded-2xl border border-line p-4">
                  <div className="mb-3">
                    <h5 className="text-sm font-semibold text-ink">
                      {t("classForm.transferTargetSessionsTitle")}
                    </h5>
                    <p className="mt-1 text-xs text-ink-muted">
                      {t("classForm.transferTargetSessionsHint")}
                    </p>
                  </div>
                  {!transferTargetClassId ? (
                    <p className="py-6 text-center text-sm text-ink-subtle">
                      {t("classForm.selectTargetToLoadSessions")}
                    </p>
                  ) : loadingTransferTargetSessions ? (
                    <p className="py-6 text-center text-sm text-ink-subtle">
                      {t("classForm.loadingTargetSessions")}
                    </p>
                  ) : transferTargetSessions.length === 0 ? (
                    <p className="py-6 text-center text-sm text-ink-subtle">
                      {t("classForm.targetHasNoOpenSessions")}
                    </p>
                  ) : (
                    <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1">
                      {transferTargetSessions.map((session) => {
                        const checked = transferTargetSessionIds.includes(
                          session.id
                        );
                        const alreadyAdded =
                          transferTargetExistingSessionIds.includes(session.id);
                        return (
                          <label
                            key={session.id}
                            className={`flex items-start gap-2 rounded-xl border border-line px-3 py-2 ${ alreadyAdded ? "cursor-not-allowed bg-gray-50/80 opacity-70 dark:bg-navy-700/40"
                                : "cursor-pointer hover:bg-surface-hover"
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={alreadyAdded}
                              onChange={(e) => {
                                setTransferTargetSessionIds((prev) =>
                                  e.target.checked
                                    ? [...prev, session.id]
                                    : prev.filter(
                                        (idItem) => idItem !== session.id
                                      )
                                );
                              }}
                              className="mt-1"
                            />
                            <div className="text-xs text-gray-600 dark:text-gray-300">
                              <div>
                                {formatSessionLabel(session, t, locale)}
                              </div>
                              {alreadyAdded && (
                                <div className="mt-1 text-[11px] text-ink-muted">
                                  {t("classForm.studentAlreadyInSession")}
                                </div>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 flex justify-end gap-2">
                <ActionButton
                  variant="secondary"
                  onClick={closeTransferModal}
                  disabled={savingTransfer}
                >
                  {t("action.cancel")}
                </ActionButton>
                <ActionButton
                  variant="primary"
                  onClick={handleTransferEnrollment}
                  disabled={!canSubmitTransfer || savingTransfer}
                >
                  {savingTransfer
                    ? t("classForm.transferring")
                    : t("classForm.transferSubmit")}
                </ActionButton>
              </div>
            </div>
          </div>
        )}

        {canManageClass && (
          <Card extra="p-6">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <div className="rounded-2xl bg-brand-50 p-2.5 text-brand-500 dark:bg-brand-500/10 dark:text-brand-300">
                  <MdCalendarMonth className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-ink">
                    {t("classForm.sessionsTitle")}
                  </h3>
                  <p className="mt-1 text-sm text-ink-muted">
                    {t("classForm.sessionsSubtitle")}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
              <span className="rounded-full bg-surface-muted px-2.5 py-1">
                {t("classForm.sessionsCreatedCount", {
                  count: existingSessions.length,
                })}
              </span>
              <span className="rounded-full bg-surface-muted px-2.5 py-1">
                {t("classForm.venuesCount", { count: venues.length })}
              </span>
            </div>

            <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  {t("classForm.fromDate")}
                </label>
                <input
                  type="date"
                  value={sessionDateFromFilter}
                  onChange={(e) => setSessionDateFromFilter(e.target.value)}
                  className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-400">
                  {t("classForm.toDate")}
                </label>
                <input
                  type="date"
                  value={sessionDateToFilter}
                  onChange={(e) => setSessionDateToFilter(e.target.value)}
                  className="w-full rounded-xl border border-line bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-brand-500"
                />
              </div>
            </div>

            {existingSessions.length > 0 && (
              <div className="mb-5 rounded-2xl border border-line bg-gray-50/60 p-4 dark:bg-canvas/20">
                <p className="mb-3 text-xs font-medium uppercase tracking-wide text-ink-muted">
                  {t("classForm.createdSessionsHeader", {
                    shown: filteredExistingSessions.length,
                    total: existingSessions.length,
                  })}
                </p>
                <div className="max-h-[320px] space-y-2 overflow-y-auto pr-1">
                  {filteredExistingSessions.length === 0 ? (
                    <p className="py-6 text-center text-xs text-ink-muted">
                      {t("classForm.noMatchingSessions")}
                    </p>
                  ) : (
                    filteredExistingSessions.map((s, i) => (
                      <div
                        key={s.id}
                        className="flex items-center justify-between rounded-xl border border-line bg-surface px-4 py-3"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-ink">
                            {t("classForm.sessionIndexTitle", {
                              index: i + 1,
                              title: s.title,
                            })}
                          </p>
                          <p className="mt-0.5 text-xs text-ink-muted">
                            {s.start_time && s.end_time
                              ? `${new Date(s.start_time).toLocaleString(
                                  locale
                                )} → ${new Date(s.end_time).toLocaleTimeString(
                                  locale,
                                  { hour: "2-digit", minute: "2-digit" }
                                )}`
                              : t("classForm.noScheduledTime")}
                            {s.duration_minutes
                              ? ` · ${t("classForm.durationMinutes", {
                                  count: s.duration_minutes,
                                })}`
                              : ""}
                            {s.location ? ` · ${s.location}` : ""}
                          </p>
                        </div>
                        <ActionButton
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteExistingSession(s.id)}
                          className="!min-h-0 !rounded-lg !p-1.5 !text-gray-400 hover:!bg-red-50 hover:!text-red-500 dark:hover:!bg-red-900/10"
                        >
                          <MdDelete className="h-4 w-4" />
                        </ActionButton>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {existingSessions.length === 0 && (
              <div className="rounded-2xl border border-dashed border-line py-10 text-center">
                <MdCalendarMonth className="mx-auto mb-2 h-8 w-8 text-gray-300 dark:text-navy-500" />
                <p className="text-sm text-ink-subtle">
                  {t("classForm.noSessions")}
                </p>
              </div>
            )}
          </Card>
        )}

        <div className="flex justify-end gap-3 pb-6">
          <ActionButton variant="secondary" onClick={() => navigate(-1)}>
            {t("action.back")}
          </ActionButton>
          {canManageClass && (
            <ActionButton type="submit" variant="primary" disabled={submitting}>
              {submitting
                ? t("action.saving")
                : isEdit
                ? t("sessionEditor.saveChanges")
                : t("classForm.createSubmit")}
            </ActionButton>
          )}
        </div>
      </form>
    </div>
  );
};

export default ClassForm;
